import type { Container } from 'pixi.js';
import { RaceEventPage, RaceEventPopup } from '../views/RaceEventPopup';
import type { RaceEventState } from '../types';
import {
  applyRaceEventReward,
  clearEndRaceEvent,
  getPlayerRank,
  joinRaceEvent,
  updateRaceEvent,
  updateLastSeenRaceLeaderboard,
} from '../RaceEventUtils';
import { openRaceEventSharePopup } from './openRaceEventSharePopup';
import { preloadLayoutsAssets } from '../../../views/preloadLayoutsAssets';
import type { RaceEventDependencies } from '../RaceEventController';

function getInitialPage(state: RaceEventState): RaceEventPage {
  if (state.startDate === null) return RaceEventPage.Join;
  return RaceEventPage.Track;
}

export async function openRaceEventPopup(
  dependencies: RaceEventDependencies,
  coinTarget: Container,
  boosterTarget: Container,
): Promise<void> {
  const { observable, currentUser, popupsController, tracking } = dependencies;

  updateRaceEvent(dependencies);
  const state = observable.getValue().raceEvent;

  if (!state) return Promise.resolve();

  if (!(await preloadLayoutsAssets(RaceEventPopup.requiredLayouts, true))) return Promise.resolve();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { resolve, promise } = Promise.withResolvers<void>();

  const popup = new RaceEventPopup({
    initialPage: getInitialPage(state),
    player: currentUser,
    stateGetter(): RaceEventState {
      return observable.getValue().raceEvent ?? state;
    },
    coinTarget,
    boosterTarget,
  });

  popup.onProgressAnimationFinished.connect(() => {
    updateLastSeenRaceLeaderboard(observable);
  });

  popup.onJoinedClicked.connect(() => {
    joinRaceEvent(observable);

    const raceEventState = observable.getValue().raceEvent;

    if (raceEventState) {
      tracking.onEventStarted(raceEventState.id);
      void popup.showPage(RaceEventPage.Track);
    } else {
      popup.onCloseClicked.emit();
    }
  });

  popup.onEndSequenceFinished.connect(async () => {
    await popupsController.closeActivePopup();
    applyRaceEventReward(dependencies);
    clearEndRaceEvent(dependencies.observable);
    tracking.onEventFinished(state, getPlayerRank(state));
    await openRaceEventSharePopup(dependencies, getPlayerRank(state));
    resolve();
  });

  popup.onCloseClicked.connect(async () => {
    await popupsController.closeActivePopup();
    resolve();
  });

  void popupsController.addPopup(popup);

  return promise;
}
